// ADR-107 Decision 4.2 — assign a candidate to a bench-purpose project.
//
// Three steps, reusing the Attach Individual wizard's own step components so the
// two flows look and behave the same:
//   1. Assignment   — `Assignment` (transfer-mode layout: read-only individual,
//                     bench-project search, engagement type + start date, and the
//                     list of projects the individual is currently on);
//   2. Roles        — `RolesSelection`;
//   3. Bench details — `BenchDetailsStep` (= shared BenchDataFields).
// What differs from the wizard is the submit: it goes through the candidate
// endpoint, which re-checks eligibility, rejects FT (the sync owns that slot),
// links the assignment to the candidate row and writes the event trail.
//
// Form state lives in AssignForm, which mounts only once the candidate's
// detail (with its server-computed prefill) is loaded, and is initialised from
// that prefill at mount. This matters for the comments field: RichTextEditor
// takes `value` as INITIAL content only, so a prefill applied after mount
// would never show.

import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Alert,
  CircularProgress, Stepper, Step, StepLabel,
} from '@mui/material';
import Assignment from '../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/Assignment';
import RolesSelection from '../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/RolesSelection';
import BenchDetailsStep from '../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/BenchDetailsStep';
import { PROJECT_PURPOSE_TYPES } from '../../../constants/projectPurposes';
import { BENCH_TYPES } from '../../../constants/benchTypes';
import { BENCH_CANDIDATE_ELIGIBILITY_LABELS, benchCandidateSourceSystemLabel } from '../../../constants/benchCandidates';
import { formatDateOnly } from './dates';
import { toPickerValue } from '../../../utils/dateOnly';

const STEPS = ['Assignment', 'Roles Selection', 'Bench Details'];

const reasonText = (reasons) => reasons.map((r) => BENCH_CANDIDATE_ELIGIBILITY_LABELS[r.code] || r.code).join('; ');

// "Salary prefilled from the Americas feed pay rate (hourly, paid semimonthly)."
const payRateNote = (payRate, sourceSystem) => {
  const context = [
    payRate.paymentType ? payRate.paymentType.toLowerCase() : null,
    payRate.paymentFrequency ? `paid ${payRate.paymentFrequency.toLowerCase()}` : null,
  ].filter(Boolean).join(', ');
  const base = `Salary prefilled from the ${benchCandidateSourceSystemLabel(sourceSystem)} feed pay rate${context ? ` (${context})` : ''}`;
  return payRate.period ? `${base}.` : `${base} — the period is not one MySM knows, please pick it.`;
};

// The prefill's project as the shape ProjectSearch displays.
const toProjectOption = (project) => (project
  ? { id: project.id, name: project.name, code: project.code || null, projectPurpose: project.projectPurpose || { code: PROJECT_PURPOSE_TYPES.BENCH } }
  : null);

const AssignForm = ({ candidate, detail, submitting, onClose, onSubmit }) => {
  const prefill = detail.assignPrefill || {};

  const [step, setStep] = useState(1);

  // Step 1 — assignment. `Assignment` in transfer mode fetches the individual (with
  // position and current projects) and hands it back through setSelectedEmployee.
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [project, setProject] = useState(() => toProjectOption(prefill.project));
  const [engagementTypeId, setEngagementTypeId] = useState(() => prefill.engagementTypeId || '');
  const [startDate, setStartDate] = useState(() => toPickerValue(prefill.startDate));

  // Step 2 — roles (empty by default, like the wizard's attach mode).
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [functionalRoles, setFunctionalRoles] = useState([]);
  const [technicalRoles, setTechnicalRoles] = useState([]);

  // Step 3 — bench details, prefilled from the candidate.
  const [benchType, setBenchType] = useState(() => prefill.benchType || BENCH_TYPES.BENCH);
  const [flagAsStar, setFlagAsStar] = useState(() => Boolean(prefill.flagAsStar));
  const [comments, setComments] = useState(() => prefill.comments || '');
  // Not prefilled on purpose: the feed's representative is the client-side delivery
  // manager, not necessarily who will represent the person on the bench.
  const [benchManager, setBenchManager] = useState(null);
  // Salary prefill comes from the feed's pay rate and is present only for users with
  // BenchSalary read (the server decides); everything stays editable.
  const payRate = prefill.salary || null;
  const [salary, setSalary] = useState(() => payRate?.amount ?? '');
  const [salaryCurrency, setSalaryCurrency] = useState(() => payRate?.currency || '');
  const [salaryPeriod, setSalaryPeriod] = useState(() => payRate?.period || '');
  const [salaryAmountType, setSalaryAmountType] = useState(() => payRate?.amountType || '');
  const [error, setError] = useState(null);

  const reasons = useMemo(() => detail.eligibility?.reasons || [], [detail]);
  const warnings = useMemo(() => reasons.filter((r) => !r.blocking), [reasons]);
  const blocking = useMemo(() => reasons.filter((r) => r.blocking), [reasons]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await onSubmit({
        projectId: project?.id,
        engagementTypeId: engagementTypeId || null,
        startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        roleIds: [...functionalRoles, ...technicalRoles],
        benchType,
        flagAsStar,
        comments: comments || null,
        benchManagerId: benchManager?.id || null,
        salary: salary === '' ? null : salary,
        salaryCurrency: salaryCurrency || null,
        salaryPeriod: salaryPeriod || null,
        salaryAmountType: salaryAmountType || null,
      });
    } catch (err) {
      setError(err);
    }
  };

  const stepOneComplete = Boolean(project?.id && engagementTypeId && startDate && startDate.isValid());
  const isLast = step === STEPS.length;
  const nextDisabled = submitting || blocking.length > 0 || (step === 1 && !stepOneComplete);

  const stepContent = () => {
    switch (step) {
      case 1:
        return (
          <Assignment
            employeeId={detail.radar?.employeeId}
            selectedEmployee={selectedEmployee}
            setSelectedEmployee={setSelectedEmployee}
            selectedDestinationProject={project}
            onProjectSelect={setProject}
            projectPurpose={PROJECT_PURPOSE_TYPES.BENCH}
            projectLabel="Bench Project"
            showProjectCode
            selectedEngagementType={engagementTypeId}
            setSelectedEngagementType={setEngagementTypeId}
            engagementTypeEditable
            disabledEngagementTypeCodes={prefill.disallowedEngagementTypeCodes || []}
            startDate={startDate}
            setStartDate={setStartDate}
          />
        );
      case 2:
        return (
          <RolesSelection
            selectedPositionForRoles={selectedPositionForRoles}
            setSelectedPositionForRoles={setSelectedPositionForRoles}
            selectedFunctionalRoles={functionalRoles}
            setSelectedFunctionalRoles={setFunctionalRoles}
            selectedTechnicalRoles={technicalRoles}
            setSelectedTechnicalRoles={setTechnicalRoles}
            initialPosition={selectedEmployee?.position}
          />
        );
      case 3:
        return (
          <>
            <BenchDetailsStep
              benchType={benchType}
              setBenchType={setBenchType}
              flagAsStar={flagAsStar}
              setFlagAsStar={setFlagAsStar}
              comments={comments}
              setComments={setComments}
              benchManager={benchManager}
              setBenchManager={setBenchManager}
              directManager={selectedEmployee?.directManager || null}
              salary={salary}
              setSalary={setSalary}
              salaryCurrency={salaryCurrency}
              setSalaryCurrency={setSalaryCurrency}
              salaryPeriod={salaryPeriod}
              setSalaryPeriod={setSalaryPeriod}
              salaryAmountType={salaryAmountType}
              setSalaryAmountType={setSalaryAmountType}
            />
            {payRate && (
              <Typography variant="caption" color="textSecondary">
                {payRateNote(payRate, candidate.sourceSystem)}
              </Typography>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Stepper activeStep={step - 1}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {blocking.length > 0 && <Alert severity="error">Not assignable: {reasonText(blocking)}</Alert>}
          {warnings.length > 0 && <Alert severity="warning">{reasonText(warnings)}</Alert>}
          {error && (
            <Alert severity="error">
              {error.message}
              {Array.isArray(error.data?.reasons) && error.data.reasons.length > 0 && <> — {reasonText(error.data.reasons)}</>}
            </Alert>
          )}

          {step === 1 && (
            <Typography variant="body2" color="textSecondary">
              Rolling off {candidate.clientName || 'client'} on {formatDateOnly(candidate.rollOffStartedDate)}
              {candidate.sowContractSourceId ? ` (SOW ${candidate.sowContractSourceId})` : ''}.
              {prefill.project?.businessUnit?.name && ` Bench project suggested from the individual's current business unit: ${prefill.project.businessUnit.name}.`}
            </Typography>
          )}

          <Box sx={{ '& > *': { gap: 2 } }}>{stepContent()}</Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={step === 1 ? onClose : () => setStep(step - 1)} disabled={submitting}>
          {step === 1 ? 'Cancel' : 'Back'}
        </Button>
        <Button variant="contained" onClick={isLast ? handleSubmit : () => setStep(step + 1)} disabled={nextDisabled}>
          {isLast ? (submitting ? 'Assigning…' : 'Assign to bench') : 'Next'}
        </Button>
      </DialogActions>
    </>
  );
};

const AssignToBenchDialog = ({ open, candidate, detail, loadingDetail, submitting, onClose, onSubmit }) => {
  // Only trust a detail that belongs to the candidate this dialog is open for.
  const detailMatches = Boolean(detail && candidate && detail.id === candidate.id);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Assign {candidate?.fullName} to bench</DialogTitle>
      {(loadingDetail || !detailMatches) ? (
        <>
          <DialogContent>
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
          </DialogActions>
        </>
      ) : (
        <AssignForm
          key={detail.id}
          candidate={candidate}
          detail={detail}
          submitting={submitting}
          onClose={onClose}
          onSubmit={onSubmit}
        />
      )}
    </Dialog>
  );
};

export default AssignToBenchDialog;
